<template>
  <div class="forms-index">
    <div class="vx-row">
      <div class="vx-col w-full">
        <vx-card>
          
          <!-- Header Actions -->
          <div class="flex justify-between items-center mb-6">
            <h3 class="text-lg font-semibold">Your Forms</h3>
            <vs-button color="success" @click="$router.push('/pages/forms/builder')">
              <i class="fas fa-plus mr-2"></i>
              Create New Form
            </vs-button>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="text-center py-8">
            <vs-loading>Loading forms...</vs-loading>
          </div>

          <!-- Forms Table -->
          <div v-else-if="forms.length > 0" class="forms-table-container">
            <vs-table class="forms-table" :data="forms" pagination max-items="10" search>
              <template slot="thead">
                <vs-th sort-key="name">Internal Name</vs-th>
                <vs-th sort-key="title">Title</vs-th>
                <vs-th sort-key="description">Description</vs-th>
                <vs-th sort-key="submissions">Submissions</vs-th>
                <vs-th>Actions</vs-th>
              </template>

              <template slot-scope="{data}">
                <vs-tr :key="indextr" v-for="(tr, indextr) in data">
                  <vs-td :data="tr.name">
                    <div class="internal-name">
                      <span v-if="tr.name" class="text-gray-700 font-medium">{{ tr.name }}</span>
                      <span v-else class="text-gray-400 italic">No internal name</span>
                    </div>
                  </vs-td>
                  
                  <vs-td :data="tr.title">
                    <div class="form-title">
                      <strong>{{ tr.title }}</strong>
                      <div class="form-meta text-xs text-gray-500 mt-1">
                        Created: {{ formatDate(tr.createdAt) }} • Fields: {{ getFieldCount(tr) }}
                      </div>
                    </div>
                  </vs-td>
                  
                  <vs-td :data="getFormSummary(tr)">
                    <div class="form-description">
                      {{ getFormSummary(tr) }}
                    </div>
                  </vs-td>
                  
                  <vs-td :data="getSubmissionsCount(tr)">
                    <div class="submissions-count">
                      <vs-button 
                        v-if="getSubmissionsCount(tr) !== '0'"
                        @click="viewSubmissions(tr.id)"
                        color="primary" 
                        type="border" 
                        size="small"
                        class="submission-link">
                        {{ getSubmissionsCount(tr) }}
                      </vs-button>
                      <span v-else class="text-gray-400">{{ getSubmissionsCount(tr) }}</span>
                    </div>
                  </vs-td>
                  
                  <vs-td>
                    <vs-dropdown>
                      <vs-button 
                        color="primary" 
                        type="border" 
                        size="small"
                        icon="more_vert">
                      </vs-button>
                      
                      <vs-dropdown-menu>
                        <vs-dropdown-item @click="editForm(tr.id)">
                          <i class="fas fa-edit mr-2"></i>
                          Edit
                        </vs-dropdown-item>
                        <vs-dropdown-item @click="viewSubmissions(tr.id)">
                          <i class="fas fa-eye mr-2"></i>
                          View Submissions
                        </vs-dropdown-item>
                        <vs-dropdown-item @click="deleteForm(tr.id)" class="text-danger">
                          <i class="fas fa-trash mr-2"></i>
                          Delete
                        </vs-dropdown-item>
                      </vs-dropdown-menu>
                    </vs-dropdown>
                  </vs-td>
                </vs-tr>
              </template>
            </vs-table>
          </div>

          <!-- Empty State -->
          <div v-else class="empty-state">
            <div class="text-center py-12 text-gray-500">
              <i class="fas fa-file-alt text-6xl mb-4"></i>
              <h3 class="text-xl font-semibold mb-2">No forms created yet</h3>
              <p class="text-gray-600 mb-6">Create your first dynamic form to get started</p>
              <vs-button color="success" @click="$router.push('/pages/forms/builder')">
                <i class="fas fa-plus mr-2"></i>
                Create Your First Form
              </vs-button>
            </div>
          </div>

          <!-- Form Examples Section -->
          <div class="mt-12" v-if="false">
            <h3 class="text-lg font-semibold mb-4">Form Examples & Patterns</h3>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div class="example-card">
                <h4 class="font-semibold mb-2">Form Layouts</h4>
                <p class="text-sm text-gray-600 mb-3">Different form layout patterns</p>
                <vs-button color="primary" size="small" @click="$router.push('/pages/forms/layouts')">
                  View Examples
                </vs-button>
              </div>
              
              <div class="example-card">
                <h4 class="font-semibold mb-2">Form Validation</h4>
                <p class="text-sm text-gray-600 mb-3">Validation examples and patterns</p>
                <vs-button color="primary" size="small" @click="$router.push('/pages/forms/validation')">
                  View Examples
                </vs-button>
              </div>
              
              <div class="example-card">
                <h4 class="font-semibold mb-2">Form Modals</h4>
                <p class="text-sm text-gray-600 mb-3">Modal forms and popups</p>
                <vs-button color="primary" size="small" @click="$router.push('/pages/forms/modals')">
                  View Examples
                </vs-button>
              </div>
            </div>
          </div>

        </vx-card>
      </div>
    </div>

    <!-- Submissions Popup -->
    <vs-popup title="Form Submissions" :active.sync="showSubmissionsPopup" fullscreen>
      <div class="submissions-popup-content">
        <div v-if="currentFormSubmissions.length > 0" class="submissions-container">
          <div class="submissions-header mb-4">
            <h3 class="text-lg font-semibold">{{ currentFormTitle }} - Submissions</h3>
            <p class="text-gray-600">Total: {{ currentFormSubmissions.length }} submissions</p>
          </div>

          <div class="submissions-table-container">
            <vs-table class="submissions-table" :data="currentFormSubmissions" pagination max-items="10">
              <template slot="thead">
                <vs-th>User</vs-th>
                <vs-th>Submission Date</vs-th>
                <vs-th v-for="field in allFormFields" :key="field.id">{{ field.title }}</vs-th>
              </template>

              <template slot-scope="{data}">
                <vs-tr :key="indextr" v-for="(tr, indextr) in data">
                  <vs-td>
                    <div class="user-info">
                      {{ getUserDisplayName(tr.submittedBy) }}
                    </div>
                  </vs-td>
                  
                  <vs-td>
                    <div class="submission-date">
                      {{ formatDate(tr.submittedAt) }}
                    </div>
                  </vs-td>
                  
                  <vs-td v-for="field in allFormFields" :key="field.id">
                    <div class="field-value">
                      {{ getFieldValue(tr.submissionData, field.id) }}
                    </div>
                  </vs-td>
                </vs-tr>
              </template>
            </vs-table>
          </div>
        </div>
        
        <div v-else class="empty-submissions">
          <div class="text-center py-12 text-gray-500">
            <i class="fas fa-inbox text-4xl mb-4"></i>
            <h3 class="text-xl font-semibold mb-2">No submissions yet</h3>
            <p class="text-gray-600">This form hasn't received any submissions yet.</p>
          </div>
        </div>
      </div>
    </vs-popup>
  </div>
</template>

<script>
import VxCard from '@/components/vx-card/VxCard.vue'
import TCVelikClient from '@/TCVelikClient/TCVelikClient.js'

export default {
  name: 'FormIndex',
  components: {
    VxCard
  },
  data() {
    return {
      forms: [],
      formSubmissions: [],
      loading: false,
      showSubmissionsPopup: false,
      currentFormSubmissions: [],
      currentFormTitle: '',
      allFormFields: [],
      users: []
    }
  },
  async mounted() {
    await this.loadForms()
  },
  methods: {
    async loadForms() {
      this.loading = true
      try {
        const query = `
          query {
            forms {
              id
              title
              name
              formMetadata
              createdAt
              updatedAt
              createdBy
              updatedBy
            }
            formSubmissions {
              id
              formId
              submittedAt
              submittedBy
            }
          }
        `

        const response = await TCVelikClient.PostRelative('/api/graphql', {
          query: query
        })

        if (response && response.data && response.data.data) {
          this.forms = response.data.data.forms || []
          this.formSubmissions = response.data.data.formSubmissions || []
        }
      } catch (error) {
        console.error('Error loading forms:', error)
        this.$vs.notify({
          title: 'Error',
          text: 'Failed to load forms',
          color: 'danger'
        })
      } finally {
        this.loading = false
      }
    },

    getFormSummary(form) {
      try {
        if (form.formMetadata) {
          const metadata = JSON.parse(form.formMetadata)
          return metadata.summary || 'No description available'
        }
      } catch (error) {
        console.error('Error parsing form metadata:', error)
      }
      return 'No description available'
    },

    getFieldCount(form) {
      try {
        if (form.formMetadata) {
          const metadata = JSON.parse(form.formMetadata)
          return metadata.fields ? metadata.fields.length : 0
        }
      } catch (error) {
        console.error('Error parsing form metadata:', error)
      }
      return 0
    },

    getSubmissionsCount(form) {
      const count = this.formSubmissions.filter(submission => submission.formId === form.id).length
      return count.toString()
    },

    formatDate(dateString) {
      if (!dateString) return 'Unknown'
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    },

    editForm(formId) {
      this.$router.push(`/pages/forms/builder?formid=${formId}`)
    },

    previewForm(formId) {
      // For now, just edit the form. In the future, this could open a preview modal
      this.$router.push(`/pages/forms/builder?formid=${formId}`)
    },

    async deleteForm(formId) {
      const form = this.forms.find(f => f.id === formId)
      if (!form) return

      this.$vs.dialog({
        type: 'confirm',
        color: 'danger',
        title: 'Delete Form',
        text: `Are you sure you want to delete "${form.title}"? This action cannot be undone.`,
        accept: async () => {
          try {
            const mutation = `
              mutation DeleteForm($id: Int!) {
                deleteForm(id: $id)
              }
            `

            const response = await TCVelikClient.PostRelative('/api/graphql', {
              query: mutation,
              variables: { id: formId }
            })

            if (response && response.data && response.data.data && response.data.data.deleteForm) {
              this.$vs.notify({
                title: 'Success',
                text: 'Form deleted successfully',
                color: 'success'
              })
              await this.loadForms() // Reload the list
            }
          } catch (error) {
            console.error('Error deleting form:', error)
            this.$vs.notify({
              title: 'Error',
              text: 'Failed to delete form',
              color: 'danger'
            })
          }
        }
      })
    },

    async viewSubmissions(formId) {
      try {
        const form = this.forms.find(f => f.id === formId)
        if (!form) return

        this.currentFormTitle = form.title
        this.showSubmissionsPopup = true

        // Load submissions for this specific form
        const query = `
          query FormSubmissionsByFormId($formId: Int!) {
            formSubmissionsByFormId(formId: $formId) {
              id
              formId
              submissionData
              submittedAt
              submittedBy
            }
          }
        `

        const response = await TCVelikClient.PostRelative('/api/graphql', {
          query: query,
          variables: { formId: formId }
        })

        if (response && response.data && response.data.data && response.data.data.formSubmissionsByFormId) {
          this.currentFormSubmissions = response.data.data.formSubmissionsByFormId
          this.buildFieldUnion()
        }
      } catch (error) {
        console.error('Error loading submissions:', error)
        this.$vs.notify({
          title: 'Error',
          text: 'Failed to load submissions',
          color: 'danger'
        })
      }
    },

    buildFieldUnion() {
      const allFields = new Map()
      
      // Collect all fields from all submissions
      this.currentFormSubmissions.forEach(submission => {
        try {
          const submissionData = JSON.parse(submission.submissionData)
          Object.keys(submissionData).forEach(fieldId => {
            if (!allFields.has(fieldId)) {
              // Try to get field title from form metadata, fallback to field ID
              const fieldTitle = this.getFieldTitleFromForm(fieldId) || `Field ${fieldId}`
              allFields.set(fieldId, {
                id: fieldId,
                title: fieldTitle
              })
            }
          })
        } catch (error) {
          console.error('Error parsing submission data:', error)
        }
      })

      this.allFormFields = Array.from(allFields.values())
    },

    getFieldTitleFromForm(fieldId) {
      // Try to find the field title from the current form's metadata
      const form = this.forms.find(f => f.id === this.currentFormSubmissions[0]?.formId)
      if (form && form.formMetadata) {
        try {
          const metadata = JSON.parse(form.formMetadata)
          if (metadata.fields && Array.isArray(metadata.fields)) {
            const field = metadata.fields.find(f => f.id.toString() === fieldId.toString())
            return field ? field.title : null
          }
        } catch (error) {
          console.error('Error parsing form metadata:', error)
        }
      }
      return null
    },

    getFieldValue(submissionDataJson, fieldId) {
      try {
        const submissionData = JSON.parse(submissionDataJson)
        return submissionData[fieldId] || '-'
      } catch (error) {
        console.error('Error parsing submission data:', error)
        return '-'
      }
    },

    getUserDisplayName(userEmail) {
      if (!userEmail) return 'Unknown'
      
      // For now, just return the email. In the future, this could be enhanced
      // to look up user's first/last name from a users table
      return userEmail
    }
  }
}
</script>

<style scoped>
.forms-table-container {
  background-color: white;
  border-radius: 0.5rem;
  overflow: hidden;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
}

.forms-table {
  margin: 0;
}

.internal-name {
  min-width: 150px;
  font-size: 0.875rem;
}

.form-title {
  min-width: 200px;
}

.form-title strong {
  color: #1f2937;
  font-size: 0.875rem;
}

.form-description {
  max-width: 300px;
  color: #6b7280;
  font-size: 0.875rem;
  line-height: 1.4;
}

.submissions-count {
  text-align: center;
  font-weight: 500;
}

.form-meta {
  color: #9ca3af;
  font-size: 0.75rem;
}

.empty-state {
  border: 2px dashed #d1d5db;
  border-radius: 0.5rem;
  background-color: #f9fafb;
}

.example-card {
  background-color: white;
  border: 1px solid #e5e7eb;
  border-radius: 0.5rem;
  padding: 1rem;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  transition: box-shadow 0.15s ease-in-out;
}

.example-card:hover {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

/* Table specific styles */
.forms-table .vs-table--tbody .vs-table--tr {
  border-bottom: 1px solid #f3f4f6;
}

.forms-table .vs-table--tbody .vs-table--tr:hover {
  background-color: #f9fafb;
}

.forms-table .vs-table--thead .vs-table--th {
  background-color: #f8fafc;
  font-weight: 600;
  color: #374151;
  border-bottom: 2px solid #e5e7eb;
}

/* Dropdown styles */
.vs-dropdown-menu {
  min-width: 120px;
}

.vs-dropdown-item {
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
}

.vs-dropdown-item:hover {
  background-color: #f3f4f6;
}

.vs-dropdown-item.text-danger {
  color: #dc2626;
}

.vs-dropdown-item.text-danger:hover {
  background-color: #fef2f2;
  color: #dc2626;
}

/* Submissions popup styles */
.submissions-popup-content {
  padding: 1rem;
  height: 100%;
  overflow-y: auto;
}

.submissions-container {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.submissions-header {
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 1rem;
}

.submissions-table-container {
  flex: 1;
  overflow: auto;
}

.submissions-table {
  margin: 0;
}

.submissions-table .vs-table--tbody .vs-table--tr {
  border-bottom: 1px solid #f3f4f6;
}

.submissions-table .vs-table--tbody .vs-table--tr:hover {
  background-color: #f9fafb;
}

.submissions-table .vs-table--thead .vs-table--th {
  background-color: #f8fafc;
  font-weight: 600;
  color: #374151;
  border-bottom: 2px solid #e5e7eb;
  white-space: nowrap;
}

.user-info {
  font-weight: 500;
  color: #1f2937;
}

.submission-date {
  color: #6b7280;
  font-size: 0.875rem;
}

.field-value {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: #374151;
}

.submission-link {
  font-weight: 500;
}

.empty-submissions {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>
